import TextButton from 'components/Luxkit/Button/TextButton'
import LineFilterAltIcon from 'components/Luxkit/Icons/line/LineFilterAltIcon'
import { rem } from 'polished'
import React from 'react'
import styled from 'styled-components'
import { OfferListFilterSubmit } from './OfferListFilterSidebar'
import cn from 'clsx'
import TextLink from 'components/Luxkit/TextLink'
import LineMapIcon from 'components/Luxkit/Icons/line/LineMapIcon'
import CSSBreakpoint from 'components/Common/CSSBreakpoint'
import { useInView } from 'react-intersection-observer'
import LineListUlIcon from 'components/Luxkit/Icons/line/LineListUlIcon'
import noop from 'lib/noop'
import OfferFilterToggles from './OfferFilterToggles'
import Heading from 'components/Luxkit/Typography/Heading'
import zIndex from 'styles/tools/z-index'
import AssistChip from 'components/Luxkit/Chips/AssistChip'
import config from 'constants/config'

const wlMobileAppStyle = config.MOBILE_APP_CUSTOMISATIONS ? `padding-top: ${rem(50)};` : ''

const ResetLink = styled(TextLink)`
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;

  &.visible {
    opacity: 1;
    pointer-events: auto;
  }
`

const MobileFilterBarNonSticky = styled(CSSBreakpoint)`
  display: flex;
  flex-direction: column;
  row-gap: ${rem(12)};
`

const MobileFilterBarSticky = styled(CSSBreakpoint)`
  display: flex;
  transition: box-shadow 0.2s, background-color 0.2s, width 0.2s, margin-left 0.2s, padding 0.2s;
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader};
  background-color: ${props => props.theme.palette.neutral.eight};
  box-shadow: ${props => props.theme.shadow.bottom.small};
  padding: ${rem(20)};
  ${wlMobileAppStyle}
  width: 100vw;
  margin-left: ${rem(-16)};

  > * + * {
    margin-left: ${rem(8)};
  }
`

const StyledResetDiv = styled.div`
  display: flex;
  column-gap: ${rem(8)};
`

const StyledHeadingDiv = styled.div`
  display: flex;
  column-gap: ${rem(48)};
  align-items: center;
  justify-content: space-between;
`

const StyledFilterToggleDiv = styled.div`
  display: flex;
  column-gap: ${rem(8)};

  &.scrollable {
    overflow-x: scroll;
    overflow-y: hidden;
  }
`

interface Props {
  availableFilters: App.OfferListFilterOptions;
  existingFilters: App.OfferListFilters;
  mapViewPath?: string;
  listViewPath?: string;
  isFilterTogglesShown?: boolean;
  showPopularHolidayTypes?: boolean;
  hasPopularHolidayTypes: boolean;
  onSortAndFilterClick?: () => void;
  onFilterApply: (values: OfferListFilterSubmit) => void;
  onFilterReset: () => void;
  onFilterToggleSeeMore: () => void;
  onViewSwitch?: () => void;
  hasSortOptions?: boolean;
  filterCount?: number;
  heading?: React.ReactNode;
  onPopularHolidayTypeFilterClick?: () => void;
}

function MobileOfferListFiltersWithToggles(props: Props) {
  const {
    availableFilters,
    existingFilters,
    mapViewPath,
    listViewPath,
    isFilterTogglesShown,
    showPopularHolidayTypes,
    hasPopularHolidayTypes,
    onSortAndFilterClick,
    onViewSwitch,
    onFilterApply,
    onFilterReset,
    onFilterToggleSeeMore,
    hasSortOptions,
    filterCount,
    heading,
    onPopularHolidayTypeFilterClick,
  } = props

  const [toLiftRef, liftInView] = useInView()

  const sortAndFilterButtonText = hasSortOptions ? 'Sort & filter' : 'Filters'

  const isFilterToggleRowScrollable = liftInView && showPopularHolidayTypes && hasPopularHolidayTypes

  return (
    <>
      <MobileFilterBarNonSticky max="xs">
        <StyledHeadingDiv>
          <Heading variant="heading2" align="start">
            {heading}
          </Heading>
          { mapViewPath &&
            <TextButton
              kind="secondary"
              variant="dark"
              fit="auto"
              to={mapViewPath}
              startIcon={<LineMapIcon />}
              onClick={onViewSwitch}
              data-testid="map-view-button">
              Map view
            </TextButton>
          }
          { listViewPath &&
            <TextButton
              kind="secondary"
              variant="dark"
              fit="mobile-full-width"
              to={listViewPath}
              startIcon={<LineListUlIcon />}
              onClick={onViewSwitch}
              data-testid="list-view-button">
              List view
            </TextButton>
          }
        </StyledHeadingDiv>
      </MobileFilterBarNonSticky>
      <div ref={toLiftRef} />
      { liftInView &&
        <MobileFilterBarNonSticky max="xs">
          <StyledFilterToggleDiv className={cn({ scrollable: isFilterToggleRowScrollable })}>
            <AssistChip
              size="medium"
              variant="filled"
              startIcon={<LineFilterAltIcon />}
              onClick={onSortAndFilterClick}
              data-testid="sort-and-filter-button"
            >
              {sortAndFilterButtonText}
            </AssistChip>
            { isFilterTogglesShown &&
              <OfferFilterToggles
                availableFilters={availableFilters?.filters}
                existingFilters={existingFilters}
                onApply={onFilterApply}
                onSeeMore={onFilterToggleSeeMore}
                showPopularHolidayTypes={showPopularHolidayTypes}
                onPopularHolidayTypeFilterClick={onPopularHolidayTypeFilterClick}
              />
            }
          </StyledFilterToggleDiv>
          <StyledResetDiv>
            { filterCount !== 0 &&
              <ResetLink
                size="medium"
                className={cn({ visible: filterCount > 0 })}
                onClick={onFilterReset}
              >
                Reset all
              </ResetLink>
            }
          </StyledResetDiv>
        </MobileFilterBarNonSticky>
      }
      { !liftInView &&
        <MobileFilterBarSticky max="xs">
          <TextButton
            kind="secondary"
            fit="mobile-full-width"
            variant="dark"
            startIcon={<LineFilterAltIcon />}
            onClick={onSortAndFilterClick}
            data-testid="sort-and-filter-button"
          >
            {sortAndFilterButtonText}
          </TextButton>
          { mapViewPath &&
            <TextButton
              kind="secondary"
              variant="dark"
              fit="mobile-full-width"
              to={mapViewPath}
              startIcon={<LineMapIcon />}
              data-testid="map-view-button">
              Map view
            </TextButton>
          }
          { listViewPath &&
            <TextButton
              kind="secondary"
              variant="dark"
              fit="mobile-full-width"
              to={listViewPath}
              startIcon={<LineListUlIcon />}
              onClick={onViewSwitch}
              data-testid="list-view-button">
              List view
            </TextButton>
          }
        </MobileFilterBarSticky>
      }
    </>
  )
}

MobileOfferListFiltersWithToggles.defaultProps = {
  existingFilters: {},
  sortOptions: [],
  onSortOpen: noop,
  onSortSelect: noop,
  onViewSwitch: noop,
}

export default MobileOfferListFiltersWithToggles
